import { useEffect, useState } from 'react'
import { useIpRights } from '../interfaces/IpRights'
import { Instruction, Renewal, RenewalState } from '../interfaces/renewal_types'
import { RenewalOverviewContext, RenewalsContext, useRenewals } from '../interfaces/Renwals'
import _ from 'lodash'
import { plusPeriod } from 'src/utils/dates'
import { DummyIpRightsProvider } from './IpRights'
import { usePatents } from 'src/patents/PatentsProvider'
import { importStatusExtraction, renewalNoticesStatus } from '../state/modern_states'

export function DummyRenewalsProvider({
  start,
  end,
  states,
  children,
}: {
  start?: string
  end?: string
  states?: RenewalState[]
  children: React.ReactNode
}) {
  const { ipRights } = useIpRights()

  const [_renewals, setRenewals] = useState([] as Renewal[])

  useEffect(() => {
    if (ipRights.length > 0 && _renewals.length === 0) {
      const today = new Date()
      const fromAYearAgo = plusPeriod(today, { years: -1 })
      const renewals = _(ipRights)
        .filter(ir => ir.status === 'Ready')
        .flatMap(
          (ir, i) =>
          [
            {
              id: 'o-' + i,
              ipRightId: ir.id,
              state: 'Open' as RenewalState,
              created: today.toISOString(),
              updated: today.toISOString(),
              instructionDueDate: plusPeriod(today, { months: 3 + (i % 4) }),
              feeDueDate: plusPeriod(today, { months: 5 + (i % 4) }),
              fee: { EUR: 567 + (i % 3) * 146 },
              isAutoPay: true,
            } as Renewal,
            {
              id: 'c-' + i,
              ipRightId: ir.id,
              state: (i % 2 === 0 ? 'Paid' : 'Cancelled') as RenewalState,
              created: fromAYearAgo,
              updated: fromAYearAgo,
              instructionDueDate: plusPeriod(fromAYearAgo, { months: 3 + (i % 4) }),
              feeDueDate: plusPeriod(fromAYearAgo, { months: 5 + (i % 4) }),
              fee: { EUR: 321 + (i % 3) * 235 },
              isAutoPay: true,
            } as Renewal,
          ]
        )
        .value()
      setRenewals(renewals)
    }
  }, [ipRights, _renewals])

  function _instruct(renewalId: string, instruction: Instruction) {
    setRenewals(_renewals.map(r => (r.id === renewalId ? { ...r, instruction, state: 'Instructed' } : r)))
  }
  function bulkInstruct(instructions: Record<string, Instruction>) {
    _(instructions)
      .toPairs()
      .forEach(([renewalId, instruction]) => {
        _instruct(renewalId, instruction)
      })
    return Promise.resolve()
  }

  const hasNoDateFilter = start === undefined || end === undefined
  // console.log({hasNoDateFilter, start, end})
  const testState = (states ?? []).length === 0 ? () => true : (state: RenewalState) => states.includes(state)
  const renewals = _renewals.filter(
    r => testState(r.state) && (hasNoDateFilter || (start <= r.instructionDueDate && r.instructionDueDate <= end)),
  )
  // console.log({_renewals, renewals})

  const value = {
    renewals,
    instruct: (renewalId: string, instruction: Instruction) => Promise.resolve(_instruct(renewalId, instruction)),
    bulkInstruct,
    isLoading: false,
  }

  return <RenewalsContext.Provider {...{ value }}>{children}</RenewalsContext.Provider>
}

export function DummyRenewalOverviewProvider({ familyMemberId, children }: { familyMemberId: number; children: React.ReactNode }) {
  return (
    <DummyIpRightsProvider>
      <DummyRenewalsProvider>
        <DummyRenewalOverviewProvider_ {...{ familyMemberId }}>{children}</DummyRenewalOverviewProvider_>
      </DummyRenewalsProvider>
    </DummyIpRightsProvider>
  )
}

function DummyRenewalOverviewProvider_({ children, familyMemberId }: { familyMemberId: number; children: React.ReactNode }) {
  const { memberById } = usePatents()
  const member = memberById[familyMemberId]

  const {renewals: allRenewals, instruct} = useRenewals()
  const { ipRightByFamilyMemberId } = useIpRights()

  if (!member) {
    return null
  }

  const ipRight = ipRightByFamilyMemberId[familyMemberId]
  const ipRightId = ipRight?.id
  const renewals = allRenewals.filter(r => r.ipRightId === ipRightId)

  // console.log({ ipRight, familyMemberId, ipRightByFamilyMemberId, renewals, allRenewals })

  const ipRightImportStatus = importStatusExtraction({ member, ipRight })
  const renewalsStatus = renewalNoticesStatus({ renewalStatus: ipRightImportStatus.status, renewals })

  const value = {
    member,
    ipRight,
    actualIpRight: ipRight,
    renewals,
    instruct,
    ipRightImportStatus,
    renewalsStatus,
    isLoading: false,
  }

  return <RenewalOverviewContext.Provider {...{value}}>{children}</RenewalOverviewContext.Provider>
}